import React, { useState } from "react";
import { motion } from "framer-motion";
import { Clock, ArrowRight, Plus } from "lucide-react";
import { SERVICES } from "../lib/services";
import { usePricing } from "../lib/usePricing";
import { ServiceDetailModal } from "./ServiceDetailModal";

const COLUMNS = [
  { key: "exterior", label: "Exteriér" },
  { key: "interior", label: "Interiér" },
  { key: "complete", label: "Komplet" },
];

const LIGHT_DIRECTIONS = [
  "radial-gradient(120% 100% at 0% 0%, rgba(255,255,255,0.05), transparent 60%)",
  "radial-gradient(120% 100% at 100% 0%, rgba(255,255,255,0.05), transparent 60%)",
  "radial-gradient(120% 100% at 50% 100%, rgba(255,255,255,0.05), transparent 60%)",
];
const LIGHT_DIRECTIONS_PREMIUM = [
  "linear-gradient(120deg, rgba(255,255,255,0.09) 0%, transparent 45%), radial-gradient(120% 100% at 0% 0%, rgba(255,255,255,0.06), transparent 60%)",
  "linear-gradient(135deg, rgba(255,255,255,0.10) 0%, transparent 50%), radial-gradient(120% 100% at 100% 0%, rgba(255,255,255,0.06), transparent 60%)",
  "linear-gradient(150deg, rgba(255,255,255,0.08) 0%, transparent 40%), radial-gradient(120% 100% at 50% 100%, rgba(255,255,255,0.06), transparent 60%)",
];

export default function Pricing({ onBook }) {
  const [detail, setDetail] = useState(null); // { tier, serviceType }
  const { services: liveServices, vehicleSurcharges } = usePricing();

  const classicData = liveServices?.classic || SERVICES.classic;
  const premiumData = liveServices?.detailing || SERVICES.detailing;

  const suvPct = Math.round(((vehicleSurcharges?.suv_mpv ?? 0.2) * 100));
  const vanPct = Math.round(((vehicleSurcharges?.van ?? 0.3) * 100));

  return (
    <section id="cenik" className="relative py-24 md:py-32 tone-pricing overflow-hidden" data-testid="pricing-section">
      <div className="lit-packages absolute inset-0 pointer-events-none" />
      <div className="relative max-w-[1180px] mx-auto px-[22px] md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, margin: "-100px" }}
          transition={{ duration: 0.7, ease: "easeOut" }} className="max-w-[720px] mb-14"
        >
          <span className="eyebrow text-akcent" data-testid="pricing-eyebrow">Ceník služeb</span>
          <h2 className="t-h1 mt-4 text-krem-100" data-testid="pricing-title">
            <span className="pricing-title-shrink">Premium, nebo Classic?</span><br className="md:hidden" /> Poradíme vám.
          </h2>
          <p className="t-body mt-4 text-tlum-dark">
            <span className="hidden md:inline">Každá služba je dostupná ve dvou úrovních. </span>
            Detaily jednotlivých úkonů najdete po kliknutí na „Zobrazit detail“.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-7">
          {COLUMNS.map((col, colIdx) => {
            const premium = premiumData[col.key];
            const classic = classicData[col.key];
            return (
              <div
                key={col.key}
                className="flex flex-col gap-3 md:gap-5"
                data-testid={`pricing-column-${col.key}`}
              >
                <div className="px-1">
                  <span className="t-label text-tlum-dark uppercase">{col.label}</span>
                </div>

                {/* CLASSIC — nahoře, pluje zezdola při scrollu */}
                <motion.div
                  initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, margin: "150px" }}
                  transition={{ duration: 0.6, delay: colIdx * 0.06, ease: [0.33, 1, 0.68, 1] }}
                  onClick={() => setDetail({ tier: "classic", serviceType: col.key })}
                  className="relative rounded-xl p-3.5 md:p-[21px] cursor-pointer transition-transform hover:-translate-y-1"
                  style={{ backgroundImage: LIGHT_DIRECTIONS[colIdx], backgroundColor: "var(--uhel-750)", border: "0.5px solid var(--linka-tmava)" }}
                  data-testid={`service-card-classic-${col.key}`}
                >
                  <span style={{ fontSize: "12.3px" }} className="t-label text-tlum-dark">Classic</span>
                  <h3 className="font-medium mt-1.5 md:mt-2" style={{ fontSize: "19px", color: "var(--krem-tmava)" }}>{classic?.name}</h3>
                  <div className="mt-2 md:mt-2.5 flex items-end gap-1.5">
                    <span className="font-semibold leading-none" style={{ fontSize: "26px", color: "var(--krem-tmava)" }}>{classic?.price?.toLocaleString("cs-CZ")}</span>
                    <span className="mb-0.5" style={{ fontSize: "15px", color: "var(--krem-tmava)", opacity: 0.75 }}>Kč</span>
                  </div>
                  <div className="mt-1.5 inline-flex items-center gap-1.5" style={{ fontSize: "15px", color: "var(--krem-tmava)", opacity: 0.85 }}>
                    <Clock className="w-4 h-4" /> {classic?.duration} min
                  </div>
                  <p className="mt-2 md:mt-2.5 text-tlum-dark leading-relaxed" style={{ fontSize: "13px" }}>{classic?.tagline}</p>
                  <div className="mt-2.5 md:mt-4">
                    <button
                      onClick={(e) => { e.stopPropagation(); setDetail({ tier: "classic", serviceType: col.key }); }}
                      className="btn-outline !rounded-lg w-full"
                      style={{ fontSize: "16px", padding: "9px 14px" }}
                      data-testid={`service-detail-btn-classic-${col.key}`}
                    >
                      <Plus className="w-3.5 h-3.5" /> Zobrazit detail
                    </button>
                  </div>
                </motion.div>

                {/* PREMIUM — dole, tmavě stříbrná s lehkým nasvícením, pluje zezdola v jednom plynulém pohybu s Classic */}
                <motion.div
                  initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false, margin: "150px" }}
                  transition={{ duration: 0.6, delay: 0.12 + colIdx * 0.06, ease: [0.33, 1, 0.68, 1] }}
                  onClick={() => setDetail({ tier: "detailing", serviceType: col.key })}
                  className="relative rounded-2xl p-4 md:p-[30px] cursor-pointer transition-transform hover:-translate-y-1 glow-premium"
                  style={{ backgroundImage: LIGHT_DIRECTIONS_PREMIUM[(colIdx + 1) % 3], backgroundColor: "var(--stribro-karta)", border: "0.5px solid var(--stribro-linka)" }}
                  data-testid={`service-card-premium-${col.key}`}
                >
                  <span className="eyebrow" style={{ fontSize: "12px", color: "var(--stribro-text)" }}>Premium</span>
                  <h3 className="font-medium mt-2 md:mt-3" style={{ fontSize: "24px", color: "var(--krem-nasedly)" }}>{premium?.name}</h3>
                  <div className="mt-2 md:mt-3 flex items-end gap-2">
                    <span className="font-semibold leading-none" style={{ fontSize: "33px", color: "var(--krem-nasedly)" }}>{premium?.price?.toLocaleString("cs-CZ")}</span>
                    <span className="mb-0.5" style={{ fontSize: "19px", color: "var(--krem-nasedly)" }}>Kč</span>
                  </div>
                  <div className="mt-2 md:mt-2.5 inline-flex items-center gap-2 font-normal text-krem-100" style={{ fontSize: "17px" }}>
                    <Clock className="w-5 h-5" /> {premium?.duration} min
                  </div>
                  <p className="mt-2.5 md:mt-3.5 text-tlum-dark leading-relaxed" style={{ fontSize: "15px" }}>{premium?.tagline}</p>
                  <div className="mt-3 md:mt-6">
                    <button
                      onClick={(e) => { e.stopPropagation(); setDetail({ tier: "detailing", serviceType: col.key }); }}
                      className="btn-silver-dark w-full"
                      style={{ fontSize: "14px", padding: "13px 19px" }}
                      data-testid={`service-detail-btn-detailing-${col.key}`}
                    >
                      <Plus className="w-4 h-4" /> Zobrazit detail
                    </button>
                  </div>
                </motion.div>
              </div>
            );
          })}
        </div>

        {/* Mobil — jedno souhrnné tlačítko Rezervovat pod poslední (Komplet Premium) kartou */}
        <div className="mt-5 md:hidden">
          <button
            onClick={() => onBook?.(null)}
            className="btn-accent w-full !justify-center"
            data-testid="pricing-mobile-book-btn"
          >
            Rezervovat <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Příplatek za typ vozu */}
        <motion.div
          initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: false }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="mt-10 text-center text-tlum-dark text-[12.5px] md:text-[16.8px]"
        >
          <p>Ceny platí pro osobní vozidla střední třídy. Pro SUV/MPV (+{suvPct} %) a VAN (+{vanPct} %) se uplatňuje příplatek.</p>
          <p className="mt-1.5">Dojezd v ceně služby do 25 km od Zelenče při objednávce nad 2 400 Kč. U menších objednávek je dojezd +400 Kč, delší vzdálenost řešíme po dohodě.</p>
        </motion.div>
      </div>

      <ServiceDetailModal detail={detail} onClose={() => setDetail(null)} onBook={onBook} />
    </section>
  );
}
